<template>
  <section class="space-y-5">
    <div class="rounded-xl border border-zinc-200 bg-white p-4 shadow-sm">
      <div class="flex flex-wrap items-start justify-between gap-3">
        <div class="min-w-0 flex-1">
          <p class="text-sm font-semibold text-zinc-800">
            Membership tier assignment
          </p>
          <p class="mt-1 text-xs text-zinc-500">
            Assign a plan tier to this member or create a member-specific custom plan.
          </p>
        </div>
        <span
          :class="assignmentBadgeClass"
          class="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold"
        >
          {{ assignmentBadgeLabel }}
        </span>
      </div>

      <div
        v-if="planErrorMessage"
        class="mt-4 rounded-lg border border-rose-200 bg-rose-50 px-3 py-3 text-sm text-rose-600"
      >
        {{ planErrorMessage }}
      </div>

      <div
        v-if="!showCustomPlanEditor"
        class="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3"
      >
        <div>
          <label class="block text-xs font-semibold uppercase tracking-wide text-zinc-500">Plan Tier</label>
          <PlatformaSelect
            v-model="selectedPlanTierId"
            class="mt-1"
            :options="planTierSelectOptions"
            :disabled="isLoadingPlan || isSavingPlanAssignment || isSavingCustomPlan"
            placeholder="Select plan tier"
          />
        </div>
        <div>
          <label class="block text-xs font-semibold uppercase tracking-wide text-zinc-500">Assignment Status</label>
          <PlatformaSelect
            v-model="selectedAssignmentStatus"
            class="mt-1"
            :options="assignmentStatusSelectOptions"
            :disabled="isLoadingPlan || isSavingPlanAssignment || isSavingCustomPlan"
            placeholder="Select status"
          />
        </div>
        <div>
          <label class="block text-xs font-semibold uppercase tracking-wide text-zinc-500">Billing Status</label>
          <PlatformaSelect
            v-model="selectedBillingStatus"
            class="mt-1"
            :options="billingStatusSelectOptions"
            :disabled="isLoadingPlan || isSavingPlanAssignment || isSavingCustomPlan"
            placeholder="Select billing status"
          />
        </div>
      </div>

      <div
        v-if="!showCustomPlanEditor"
        class="mt-4 flex flex-wrap items-center gap-2"
      >
        <button
          type="button"
          class="rounded-lg bg-zinc-900 px-4 py-2 text-sm font-semibold text-white hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-60"
          :disabled="isLoadingPlan || isSavingPlanAssignment || isSavingCustomPlan"
          @click="saveTierAssignment"
        >
          {{ isSavingPlanAssignment ? 'Saving...' : 'Save Assignment' }}
        </button>
        <button
          type="button"
          class="rounded-lg border border-[#2D8CFF] bg-white px-4 py-2 text-sm font-semibold text-[#2D8CFF] hover:bg-[#2D8CFF]/5 disabled:cursor-not-allowed disabled:opacity-60"
          :disabled="isLoadingPlan || isSavingPlanAssignment || isSavingCustomPlan"
          @click="openCustomPlanEditor"
        >
          {{ hasExistingCustomPlan ? 'Edit Custom Plan' : 'Create Custom Plan' }}
        </button>
        <Transition
          enter-active-class="transition duration-200 ease-out"
          enter-from-class="opacity-0 -translate-y-0.5"
          enter-to-class="opacity-100 translate-y-0"
          leave-active-class="transition duration-150 ease-in"
          leave-from-class="opacity-100 translate-y-0"
          leave-to-class="opacity-0 -translate-y-0.5"
        >
          <span
            v-if="planNoticeMessage"
            class="inline-flex items-center text-sm font-semibold text-emerald-600"
          >
            {{ planNoticeMessage }}
          </span>
        </Transition>
      </div>

      <Transition
        enter-active-class="transition duration-200 ease-out"
        enter-from-class="opacity-0 -translate-y-1"
        enter-to-class="opacity-100 translate-y-0"
        leave-active-class="transition duration-150 ease-in"
        leave-from-class="opacity-100 translate-y-0"
        leave-to-class="opacity-0 -translate-y-1"
      >
        <div
          v-if="showCustomPlanEditor"
          class="mt-4 rounded-xl bg-zinc-50 p-4"
        >
          <div class="mb-4 flex items-center justify-between gap-2">
            <div>
              <p class="text-base font-semibold text-zinc-900">
                {{ editingCustomPlan ? 'Edit custom plan tier' : 'Create custom plan tier' }}
              </p>
              <p class="text-xs text-zinc-500">
                Build this member-specific plan using the same tier options from Plan Tiers.
              </p>
            </div>
            <button
              type="button"
              class="rounded-md border border-zinc-300 bg-white px-2.5 py-1 text-xs font-semibold text-zinc-700 hover:bg-zinc-50"
              :disabled="isSavingCustomPlan"
              @click="closeCustomPlanEditor"
            >
              Cancel
            </button>
          </div>

          <div class="rounded-xl bg-white p-4">
            <h4 class="text-xl font-semibold text-zinc-900">
              Customize tier
            </h4>

            <div class="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
              <div>
                <label class="block text-xs font-semibold uppercase tracking-wide text-zinc-500">Plan Name</label>
                <input
                  v-model.trim="customPlanForm.name"
                  type="text"
                  maxlength="190"
                  class="mt-1 h-10 w-full rounded-md border border-zinc-300 bg-white px-3 text-sm text-zinc-800 focus:outline-none focus:ring-0"
                  placeholder="Custom plan name"
                >
              </div>
              <div>
                <label class="block text-xs font-semibold uppercase tracking-wide text-zinc-500">Tier Slug</label>
                <input
                  v-model.trim="customPlanForm.slug"
                  type="text"
                  maxlength="190"
                  class="mt-1 h-10 w-full rounded-md border border-zinc-300 bg-white px-3 text-sm text-zinc-800 focus:outline-none focus:ring-0"
                  placeholder="custom-tier-slug"
                  @input="customPlanSlugManuallyEdited = true"
                >
              </div>
            </div>

            <div class="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
              <div>
                <label class="block text-xs font-semibold uppercase tracking-wide text-zinc-500">Billing Cadence</label>
                <PlatformaSelect
                  v-model="customPlanForm.billingCadence"
                  class="mt-1"
                  :options="customBillingCadenceOptions"
                  :disabled="isSavingCustomPlan"
                />
              </div>
              <div>
                <label class="block text-xs font-semibold uppercase tracking-wide text-zinc-500">Tier Status</label>
                <PlatformaSelect
                  v-model="customPlanForm.status"
                  class="mt-1"
                  :options="planTierStatusSelectOptions"
                  :disabled="isSavingCustomPlan"
                />
              </div>
            </div>

            <div class="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
              <div>
                <label class="block text-xs font-semibold uppercase tracking-wide text-zinc-500">Price</label>
                <div class="relative mt-1">
                  <span class="pointer-events-none absolute inset-y-0 left-3 inline-flex items-center text-sm text-zinc-500">$</span>
                  <input
                    v-model.trim="customPlanForm.price"
                    type="number"
                    min="0"
                    step="0.01"
                    :disabled="isSavingCustomPlan || customPlanForm.billingCadence === 'free_forever'"
                    class="h-10 w-full appearance-none rounded-md border border-zinc-300 bg-white pl-8 pr-3 text-sm text-zinc-800 focus:outline-none focus:ring-0 disabled:bg-zinc-100"
                  >
                </div>
              </div>
              <div>
                <label class="block text-xs font-semibold uppercase tracking-wide text-zinc-500">Currency</label>
                <input
                  v-model.trim="customPlanForm.currencyCode"
                  type="text"
                  maxlength="3"
                  :disabled="isSavingCustomPlan"
                  class="mt-1 h-10 w-full rounded-md border border-zinc-300 bg-white px-3 text-sm uppercase text-zinc-800 focus:outline-none focus:ring-0"
                >
              </div>
            </div>

            <div class="mt-3">
              <label class="block text-xs font-semibold uppercase tracking-wide text-zinc-500">Description (optional)</label>
              <textarea
                v-model.trim="customPlanForm.description"
                rows="3"
                maxlength="4000"
                :disabled="isSavingCustomPlan"
                class="mt-1 w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-800 focus:outline-none focus:ring-0"
                placeholder="Describe this custom plan."
              />
            </div>
          </div>

          <div class="mt-4 rounded-xl bg-white p-4">
            <h4 class="text-lg font-semibold text-zinc-900">
              Cover image (optional)
            </h4>
            <p class="mt-1 text-xs text-zinc-500">
              Recommended size: 460 by 200 pixels.
            </p>
            <input
              v-model.trim="customPlanForm.coverImageUrl"
              type="url"
              maxlength="2000"
              placeholder="https://..."
              :disabled="isSavingCustomPlan"
              class="mt-2 h-10 w-full rounded-md border border-zinc-300 bg-white px-3 text-sm text-zinc-800 focus:outline-none focus:ring-0"
            >
          </div>

          <div class="mt-4 rounded-xl bg-white p-4">
            <button
              type="button"
              class="flex w-full items-center justify-between text-left"
              @click="customBenefitsOpen = !customBenefitsOpen"
            >
              <div>
                <h4 class="text-lg font-semibold text-zinc-900">
                  Tier benefits (optional)
                </h4>
                <p class="mt-1 text-xs text-zinc-500">
                  Select membership product feature instances for this tier.
                </p>
              </div>
              <ChevronUpIcon
                v-if="customBenefitsOpen"
                class="h-5 w-5 text-zinc-600"
              />
              <ChevronDownIcon
                v-else
                class="h-5 w-5 text-zinc-600"
              />
            </button>

            <div
              v-if="customBenefitsOpen"
              class="mt-3"
            >
              <div
                v-if="isLoadingProductFeatures"
                class="rounded-md bg-zinc-50 px-3 py-2 text-sm text-zinc-500"
              >
                Loading membership products...
              </div>
              <div
                v-else-if="!productFeatureRows.length"
                class="rounded-md bg-zinc-50 px-3 py-2 text-sm text-zinc-500"
              >
                No membership product feature instances were found.
              </div>
              <div
                v-else
                class="space-y-2"
              >
                <label
                  v-for="feature in productFeatureRows"
                  :key="`member-custom-feature-${feature.instanceId}`"
                  class="flex items-start gap-3 rounded-md bg-zinc-50 px-3 py-2 hover:bg-zinc-100"
                >
                  <input
                    v-model="customSelectedFeatureIds"
                    type="checkbox"
                    :value="feature.instanceId"
                    :disabled="isSavingCustomPlan"
                    class="mt-0.5 h-4 w-4 rounded border-zinc-300 text-[#2d8bff] focus:ring-0"
                  >
                  <div class="min-w-0">
                    <p class="truncate text-sm font-semibold text-zinc-800">{{ feature.displayName }}</p>
                    <p class="truncate text-xs text-zinc-500">{{ feature.slugLabel }}</p>
                  </div>
                </label>
              </div>
            </div>
          </div>

          <div class="mt-4 rounded-xl bg-white p-4">
            <div class="flex items-start justify-between gap-4">
              <div>
                <h4 class="text-lg font-semibold text-zinc-900">
                  Free trial
                </h4>
                <p class="mt-1 text-xs text-zinc-500">
                  Allow members to try this tier before charging them.
                </p>
              </div>
              <button
                type="button"
                class="relative inline-flex h-8 w-14 shrink-0 items-center rounded-full transition-colors"
                :class="[
                  customPlanForm.freeTrialEnabled ? 'bg-[#2D8CFF]' : 'bg-zinc-300',
                  ['free_forever', 'one_time'].includes(customPlanForm.billingCadence) ? 'cursor-not-allowed opacity-50' : '',
                ]"
                :disabled="isSavingCustomPlan || ['free_forever', 'one_time'].includes(customPlanForm.billingCadence)"
                @click="toggleCustomFreeTrial"
              >
                <span
                  class="inline-block h-6 w-6 transform rounded-full bg-white shadow transition"
                  :class="customPlanForm.freeTrialEnabled ? 'translate-x-7' : 'translate-x-1'"
                />
              </button>
            </div>
            <div
              v-if="customPlanForm.freeTrialEnabled"
              class="mt-3"
            >
              <label class="block text-xs font-semibold uppercase tracking-wide text-zinc-500">Trial Days</label>
              <input
                v-model.trim="customPlanForm.trialDays"
                type="number"
                min="1"
                step="1"
                :disabled="isSavingCustomPlan || ['free_forever', 'one_time'].includes(customPlanForm.billingCadence)"
                class="mt-1 h-10 w-44 rounded-md border border-zinc-300 bg-white px-3 text-sm text-zinc-800 focus:outline-none focus:ring-0 disabled:bg-zinc-100"
              >
            </div>
          </div>

          <div class="mt-4 rounded-xl bg-white p-4">
            <button
              type="button"
              class="flex w-full items-center justify-between text-left"
              @click="customAdvancedOpen = !customAdvancedOpen"
            >
              <h4 class="text-lg font-semibold text-zinc-900">
                Advanced
              </h4>
              <ChevronUpIcon
                v-if="customAdvancedOpen"
                class="h-5 w-5 text-zinc-600"
              />
              <ChevronDownIcon
                v-else
                class="h-5 w-5 text-zinc-600"
              />
            </button>
            <div
              v-if="customAdvancedOpen"
              class="mt-4"
            >
              <p class="text-sm font-semibold text-zinc-900">
                Tier settings
              </p>
              <label class="mt-2 flex items-center gap-2 text-sm text-zinc-700">
                <input
                  v-model="customPlanForm.askShippingAddress"
                  type="checkbox"
                  class="h-4 w-4 rounded border-zinc-300 text-[#2D8CFF] focus:ring-[#2D8CFF]"
                >
                Ask members for a shipping address in checkout
              </label>
              <label class="mt-2 flex items-center gap-2 text-sm text-zinc-700">
                <input
                  v-model="customPlanForm.limitMembersEnabled"
                  type="checkbox"
                  class="h-4 w-4 rounded border-zinc-300 text-[#2D8CFF] focus:ring-[#2D8CFF]"
                >
                Limit the number of members who can join this tier
              </label>
              <div
                v-if="customPlanForm.limitMembersEnabled"
                class="mt-2"
              >
                <input
                  v-model.trim="customPlanForm.memberLimitCount"
                  type="number"
                  min="1"
                  step="1"
                  class="h-10 w-44 rounded-md border border-zinc-300 bg-white px-3 text-sm text-zinc-800 focus:outline-none focus:ring-0"
                  placeholder="Member limit"
                >
              </div>
            </div>
          </div>

          <div class="mt-4 rounded-xl bg-white p-4">
            <h4 class="text-lg font-semibold text-zinc-900">
              Payment processor integration
            </h4>
            <p class="mt-1 text-xs text-zinc-500">
              Choose which processor receives charges for this tier.
            </p>
            <PlatformaSelect
              v-model="customPlanForm.paymentAccountId"
              class="mt-2"
              :options="paymentAccountOptions"
              :disabled="isSavingCustomPlan || isLoadingPaymentSettings"
            />
          </div>

          <div class="mt-4 flex flex-wrap items-center justify-end gap-2">
            <button
              type="button"
              class="rounded-lg border border-zinc-300 bg-white px-4 py-2 text-sm font-semibold text-zinc-700 hover:bg-zinc-50"
              :disabled="isSavingCustomPlan"
              @click="closeCustomPlanEditor"
            >
              Back
            </button>
            <button
              type="button"
              class="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-60"
              :disabled="isSavingCustomPlan"
              @click="saveCustomPlan"
            >
              {{ isSavingCustomPlan ? 'Saving Custom Plan...' : 'Save Custom Plan' }}
            </button>
          </div>
        </div>
      </Transition>
    </div>

    <div class="rounded-xl border border-zinc-200 bg-white p-4 shadow-sm">
      <div class="flex flex-wrap items-start justify-between gap-3">
        <div class="min-w-0 flex-1">
          <p class="text-sm font-semibold text-zinc-800">
            Next scheduled charge attempt
          </p>
          <p
            class="mt-1 text-sm text-zinc-700"
          >
            {{ nextChargePrimaryText }}
          </p>
          <p
            v-if="nextChargeSecondaryText"
            class="mt-1 text-xs text-zinc-500"
          >
            {{ nextChargeSecondaryText }}
          </p>
        </div>

        <div class="min-w-0 text-right">
          <span
            :class="nextChargeBadgeClass"
            class="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold"
          >
            {{ nextChargeBadgeLabel }}
          </span>
        </div>
      </div>

      <div class="mt-3">
        <div class="flex flex-wrap items-center gap-2">
          <button
            type="button"
            class="h-10 rounded-lg bg-blue-600 px-4 text-sm font-semibold text-white hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-60"
            :disabled="isSavingRescheduleNextCharge || isLoadingPlan || isLoading"
            @click="showReschedulePicker = true"
          >
            {{ isSavingRescheduleNextCharge ? 'Saving...' : 'Reschedule next charge' }}
          </button>
          <button
            v-if="showReschedulePicker"
            type="button"
            class="h-10 rounded-lg border border-zinc-300 bg-white px-4 text-sm font-semibold text-zinc-700 hover:bg-zinc-50"
            :disabled="isSavingRescheduleNextCharge"
            @click="closeReschedulePicker"
          >
            Cancel
          </button>
        </div>
        <div
          v-if="showReschedulePicker"
          class="mt-3 max-w-[560px] rounded-xl border border-zinc-200 bg-zinc-50 p-3"
        >
          <NewsfeedPostScheduler
            :min-lead-minutes="5"
            @schedule="handleRescheduleSchedule"
          />
        </div>
        <p
          v-if="rescheduleNextChargeError"
          class="mt-2 text-xs text-rose-600"
        >
          {{ rescheduleNextChargeError }}
        </p>
        <p
          v-else-if="rescheduleNextChargeNotice"
          class="mt-2 text-xs text-emerald-600"
        >
          {{ rescheduleNextChargeNotice }}
        </p>
      </div>
    </div>

    <div class="rounded-xl border border-zinc-200 bg-white p-4 shadow-sm">
      <div class="flex flex-wrap items-center justify-between gap-3">
        <div class="min-w-0 flex-1">
          <p class="text-sm font-semibold text-zinc-800">
            Cancel subscription
          </p>
          <p class="mt-1 text-sm text-zinc-600">
            Schedule period-end cancellation or cancel immediately for this member.
          </p>
          <div class="mt-2 flex flex-wrap items-center gap-2">
            <span class="text-xs font-semibold uppercase tracking-wide text-zinc-500">Current status</span>
            <span
              :class="creatorCancellationStatusBadgeClass"
              class="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold"
            >
              {{ creatorCancellationStatusLabel }}
            </span>
            <span
              v-if="creatorCancellationEffectiveAtText"
              class="text-xs text-zinc-500"
            >
              {{ creatorCancellationEffectiveAtText }}
            </span>
          </div>
        </div>
        <div class="flex flex-wrap items-center gap-2">
          <button
            v-if="canCreatorReverseCancellation"
            type="button"
            class="rounded-lg border border-emerald-300 bg-white px-4 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-60"
            :disabled="isLoadingPlan || isSavingPlanAssignment || isSavingCustomPlan || isSubmittingCancellation"
            @click="reverseCreatorCancellation"
          >
            Reverse Cancellation
          </button>
          <button
            v-if="planAssignment && !isSubscriptionCanceled"
            type="button"
            class="rounded-lg border border-rose-300 bg-white px-4 py-2 text-sm font-semibold text-rose-600 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-60"
            :disabled="isLoadingPlan || isSavingPlanAssignment || isSavingCustomPlan || isSubmittingCancellation"
            @click="cancelMemberSubscription"
          >
            {{ showCancelSubscriptionOptions ? 'Close options' : 'Cancel Subscription' }}
          </button>
        </div>
      </div>

      <div
        v-if="planAssignment && isSubscriptionCanceled"
        class="mt-3 rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-3 text-sm text-zinc-600"
      >
        <p>This subscription is already canceled.</p>
        <button
          type="button"
          class="mt-3 rounded-lg border border-emerald-300 bg-white px-4 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-60"
          :disabled="isLoadingPlan || isSavingPlanAssignment || isSavingCustomPlan || isSubmittingCancellation"
          @click="restoreCanceledSubscriptionByCreator"
        >
          Restore Account
        </button>
      </div>

      <Transition
        enter-active-class="transition duration-200 ease-out"
        enter-from-class="opacity-0 -translate-y-1"
        enter-to-class="opacity-100 translate-y-0"
        leave-active-class="transition duration-150 ease-in"
        leave-from-class="opacity-100 translate-y-0"
        leave-to-class="opacity-0 -translate-y-1"
      >
        <div
          v-if="showCancelSubscriptionOptions && planAssignment && !isSubscriptionCanceled"
          class="mt-3 rounded-xl border border-rose-200 bg-rose-50/40 p-4"
        >
          <p class="text-sm font-semibold text-zinc-900">
            Cancellation options
          </p>

          <div class="mt-3 space-y-2">
            <label class="flex items-start gap-2 text-sm text-zinc-700">
              <input
                v-model="cancellationMode"
                class="mt-0.5 h-4 w-4 rounded border-zinc-300 text-rose-600 focus:ring-rose-500"
                type="radio"
                value="period_end"
              >
              <span>End at next scheduled charge time (keep current paid access until then).</span>
            </label>
            <label class="flex items-start gap-2 text-sm text-zinc-700">
              <input
                v-model="cancellationMode"
                class="mt-0.5 h-4 w-4 rounded border-zinc-300 text-rose-600 focus:ring-rose-500"
                type="radio"
                value="immediate"
              >
              <span>Cancel now (member immediately loses access).</span>
            </label>
          </div>

          <div class="mt-4 space-y-2">
            <label class="flex items-start gap-2 text-sm text-zinc-700">
              <input
                v-model="showCancellationRestoreBanner"
                class="mt-0.5 h-4 w-4 rounded border-zinc-300 text-rose-600 focus:ring-rose-500"
                type="checkbox"
              >
              <span>Show cancellation restore banner in member account.</span>
            </label>
            <label class="flex items-start gap-2 text-sm text-zinc-700">
              <input
                v-model="allowCancellationRestore"
                class="mt-0.5 h-4 w-4 rounded border-zinc-300 text-rose-600 focus:ring-rose-500"
                type="checkbox"
              >
              <span>Allow account restoration after cancellation request.</span>
            </label>
          </div>

          <p
            v-if="cancellationOptionsError"
            class="mt-3 text-xs text-rose-600"
          >
            {{ cancellationOptionsError }}
          </p>

          <div class="mt-4 flex flex-wrap items-center gap-2">
            <button
              type="button"
              class="rounded-lg border border-zinc-300 bg-white px-4 py-2 text-sm font-semibold text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60"
              :disabled="isSubmittingCancellation"
              @click="showCancelSubscriptionOptions = false"
            >
              Keep subscription
            </button>
            <button
              type="button"
              class="rounded-lg bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-500 disabled:cursor-not-allowed disabled:opacity-60"
              :disabled="isSubmittingCancellation"
              @click="submitCreatorCancellation"
            >
              {{ isSubmittingCancellation ? 'Saving...' : 'Confirm cancellation' }}
            </button>
          </div>
        </div>
      </Transition>
    </div>

    <div class="rounded-xl border border-zinc-200 bg-white p-4 shadow-sm">
      <div class="flex flex-wrap items-center gap-3 border-b border-zinc-200 pb-3">
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Search event, order, checkout, payment..."
          class="h-10 min-w-[260px] rounded-lg border border-zinc-300 bg-white px-3 text-sm text-zinc-800 focus:outline-none focus:ring-0"
        >
        <PlatformaSelect
          v-model="selectedEventType"
          class="h-10 min-w-[220px]"
          :options="eventTypeSelectOptions"
          :disabled="isLoading"
          placeholder="All events"
        />
        <PlatformaSelect
          v-model="selectedStatus"
          class="h-10 min-w-[220px]"
          :options="statusSelectOptions"
          :disabled="isLoading"
          placeholder="All statuses"
        />
        <button
          type="button"
          class="h-10 rounded-lg bg-zinc-100 px-4 text-sm font-semibold text-zinc-800 hover:bg-zinc-200"
          :disabled="isLoading"
          @click="loadSubscriptionData"
        >
          {{ isLoading ? 'Loading...' : 'Apply' }}
        </button>
      </div>

      <div class="pt-4">
        <div
          v-if="errorMessage"
          class="rounded-lg border border-rose-200 bg-rose-50 px-3 py-3 text-sm text-rose-600"
        >
          {{ errorMessage }}
        </div>

        <div
          v-else
          class="overflow-x-auto rounded-xl border border-zinc-200"
        >
          <table class="min-w-full divide-y divide-zinc-200">
            <thead class="bg-zinc-50">
              <tr>
                <th class="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-zinc-500">
                  Date
                </th>
                <th class="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-zinc-500">
                  Event
                </th>
                <th class="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-zinc-500">
                  Status
                </th>
                <th class="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-zinc-500">
                  Amount
                </th>
                <th class="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-zinc-500">
                  Details
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-zinc-100 bg-white">
              <tr v-if="isLoading">
                <td
                  colspan="5"
                  class="px-4 py-8 text-sm text-zinc-500"
                >
                  Loading subscription activity...
                </td>
              </tr>
              <tr v-else-if="!eventRows.length">
                <td
                  colspan="5"
                  class="px-4 py-8 text-sm text-zinc-500"
                >
                  No subscription payment attempts found for this member.
                </td>
              </tr>
              <tr
                v-for="event in eventRows"
                v-else
                :key="`member-subscription-event-${event.id}`"
                class="hover:bg-zinc-50"
              >
                <td class="px-4 py-2 text-sm text-zinc-700 whitespace-nowrap">
                  {{ formatDateTime(event.occurredAt) }}
                </td>
                <td class="px-4 py-2 text-sm text-zinc-900">
                  {{ event.eventType || '-' }}
                </td>
                <td class="px-4 py-2">
                  <span
                    :class="statusBadgeClass(event.status)"
                    class="inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset"
                  >
                    {{ event.status || '-' }}
                  </span>
                </td>
                <td class="px-4 py-2 text-sm text-zinc-900 whitespace-nowrap">
                  {{ formatAmount(event.amountCents, event.currencyCode) }}
                </td>
                <td class="max-w-[360px] px-4 py-2 text-xs text-zinc-600">
                  <p
                    v-if="event.details?.planTierName"
                    class="max-w-[340px] truncate"
                  >
                    Tier: {{ event.details.planTierName }}
                  </p>
                  <p
                    v-if="event.details?.billingReason"
                    class="max-w-[340px] truncate"
                  >
                    Reason: {{ event.details.billingReason }}
                  </p>
                  <p
                    v-if="event.details?.invoicePublicId"
                    class="max-w-[340px] truncate"
                  >
                    Invoice: {{ event.details.invoicePublicId }}
                  </p>
                  <p
                    v-if="event.details?.attemptPublicId"
                    class="max-w-[340px] truncate"
                  >
                    Attempt: {{ event.details.attemptPublicId }}
                  </p>
                  <p
                    v-if="event.details?.attemptNumber"
                    class="max-w-[340px] truncate"
                  >
                    Attempt #{{ event.details.attemptNumber }}
                  </p>
                  <p
                    v-if="event.details?.providerAccountRef"
                    class="max-w-[340px] truncate"
                  >
                    Processor Ref: {{ event.details.providerAccountRef }}
                  </p>
                  <p
                    v-if="event.details?.paymentPublicId"
                    class="max-w-[340px] truncate"
                  >
                    Payment: {{ event.details.paymentPublicId }}
                  </p>
                  <p
                    v-if="event.details?.failureReason"
                    class="max-w-[340px] truncate text-rose-600"
                  >
                    Failure: {{ event.details.failureReason }}
                  </p>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-zinc-200 pt-3">
        <div class="text-sm text-zinc-600">
          Showing
          <span class="font-medium text-zinc-800">{{ paginationStart }}</span>
          to
          <span class="font-medium text-zinc-800">{{ paginationEnd }}</span>
          of
          <span class="font-medium text-zinc-800">{{ totalCount }}</span>
        </div>

        <div class="flex items-center gap-3">
          <label class="flex items-center gap-2 text-sm text-zinc-600">
            <span>Rows per page:</span>
            <PlatformaSelect
              v-model.number="pageSize"
              class="min-w-[110px]"
              :options="pageSizeSelectOptions"
              :disabled="isLoading"
              menu-placement="top"
            />
          </label>

          <button
            type="button"
            class="inline-flex items-center rounded-lg border border-zinc-300 bg-white px-3 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-60"
            :disabled="currentPage <= 1 || isLoading"
            @click="prevPage"
          >
            Previous
          </button>
          <button
            type="button"
            class="inline-flex items-center rounded-lg border border-zinc-300 bg-white px-3 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-60"
            :disabled="currentPage >= totalPages || isLoading"
            @click="nextPage"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, onBeforeUnmount, reactive, ref, watch } from 'vue';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/vue/24/solid';
import PlatformaSelect from '@/Components/PlatformaSelect.vue';
import NewsfeedPostScheduler from '@/Components/NewsfeedPostScheduler.vue';

const props = defineProps({
  memberId: {
    type: [String, Number],
    default: null,
  },
});

const apiProxyUrl = process.env.VUE_APP_API_PROXY_URL;
const apiBackendUrl = process.env.VUE_APP_PLATFORMA_API_BACKEND_URL;

const isLoading = ref(false);
const errorMessage = ref('');
const nextCharge = ref(null);
const isSavingRescheduleNextCharge = ref(false);
const rescheduleNextChargeLocal = ref('');
const rescheduleNextChargeError = ref('');
const rescheduleNextChargeNotice = ref('');
const showReschedulePicker = ref(false);
const eventRows = ref([]);
const eventTypeOptions = ref([]);
const statusOptions = ref([]);
const memberProfile = ref(null);

const isLoadingPlan = ref(false);
const isLoadingPaymentSettings = ref(false);
const isLoadingProductFeatures = ref(false);
const isSavingPlanAssignment = ref(false);
const isSavingCustomPlan = ref(false);
const planErrorMessage = ref('');
const planNoticeMessage = ref('');
const planTiers = ref([]);
const planAssignment = ref(null);
const paymentAccounts = ref([]);
const productFeatureRows = ref([]);
const selectedPlanTierId = ref('');
const selectedAssignmentStatus = ref('active');
const selectedBillingStatus = ref('pending_payment');
const customSelectedFeatureIds = ref([]);
const showCustomPlanEditor = ref(false);
const customBenefitsOpen = ref(false);
const customAdvancedOpen = ref(false);
const customPlanSlugManuallyEdited = ref(false);
const showCancelSubscriptionOptions = ref(false);
const isSubmittingCancellation = ref(false);
const cancellationMode = ref('period_end');
const showCancellationRestoreBanner = ref(true);
const allowCancellationRestore = ref(true);
const cancellationOptionsError = ref('');
let planNoticeTimer = null;

const customPlanForm = reactive({
  customTierId: 0,
  name: '',
  slug: '',
  billingCadence: 'monthly',
  price: '0.00',
  currencyCode: 'USD',
  freeTrialEnabled: false,
  trialDays: '0',
  status: 'active',
  description: '',
  coverImageUrl: '',
  askShippingAddress: false,
  limitMembersEnabled: false,
  memberLimitCount: '',
  paymentAccountId: '',
});

const searchQuery = ref('');
const selectedEventType = ref('');
const selectedStatus = ref('');
const currentPage = ref(1);
const pageSize = ref(25);
const totalCount = ref(0);
const totalPages = ref(1);

const paginationStart = computed(() => {
  if (!totalCount.value) return 0;
  return (currentPage.value - 1) * pageSize.value + 1;
});

const paginationEnd = computed(() => {
  if (!totalCount.value) return 0;
  return Math.min(currentPage.value * pageSize.value, totalCount.value);
});

const nextChargeBadgeLabel = computed(() => {
  if (isLoading.value) return 'Loading';
  if (!nextCharge.value) return 'Not Scheduled';
  const status = String(nextCharge.value?.status || '').trim().toLowerCase();
  if (['failed', 'canceled', 'cancelled'].includes(status)) return 'Retry';
  if (status === 'processing') return 'Processing';
  return 'Scheduled';
});

const nextChargeBadgeClass = computed(() => {
  if (!nextCharge.value) return 'bg-zinc-100 text-zinc-600';
  const status = String(nextCharge.value?.status || '').trim().toLowerCase();
  if (['failed', 'canceled', 'cancelled'].includes(status)) return 'bg-amber-100 text-amber-700';
  if (status === 'processing') return 'bg-blue-100 text-blue-700';
  return 'bg-emerald-100 text-emerald-700';
});

const nextChargePrimaryText = computed(() => {
  if (isLoading.value) return 'Loading schedule...';
  if (!nextCharge.value) return 'No scheduled subscription charge attempt found.';
  const dueAtRaw = String(nextCharge.value?.dueAt || '').trim();
  if (!dueAtRaw) return 'No scheduled subscription charge attempt found.';
  const dueAt = new Date(dueAtRaw);
  if (Number.isNaN(dueAt.getTime())) return 'No scheduled subscription charge attempt found.';
  const amountText = formatAmount(nextCharge.value?.amountCents, nextCharge.value?.currencyCode);
  return `${amountText} scheduled for ${dueAt.toLocaleString()}.`;
});

const nextChargeSecondaryText = computed(() => {
  if (!nextCharge.value) return '';
  const billingReason = String(nextCharge.value?.billingReason || '').trim().toLowerCase();
  const reasonLabel = billingReason ? billingReason.replace(/_/g, ' ') : 'renewal';
  const source = String(nextCharge.value?.source || '').trim().toLowerCase();
  const sourceLabel = source === 'invoice_schedule' ? 'invoice schedule' : 'subscription schedule';
  const retryPhase = String(nextCharge.value?.retryPhase || '').trim().toLowerCase();
  const cadence = String(nextCharge.value?.billingCadence || '').trim().toLowerCase();
  const cadenceLabel = cadence ? cadence : 'billing cadence';
  const tierName = String(nextCharge.value?.planTierName || '').trim();
  const lead = tierName ? `${tierName} (${cadenceLabel})` : cadenceLabel;
  if (retryPhase) {
    return `${lead} | ${reasonLabel} | ${sourceLabel} | retry phase: ${retryPhase}`;
  }
  return `${lead} | ${reasonLabel} | ${sourceLabel}`;
});

const humanizeOptionLabel = (token, fallbackLabel) => {
  const value = String(token || '').trim();
  if (!value) return String(fallbackLabel || '').trim();
  return value
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const eventTypeSelectOptions = computed(() => (
  [{ value: '', label: 'All events' }].concat(
    (Array.isArray(eventTypeOptions.value) ? eventTypeOptions.value : []).map((value) => ({
      value: String(value),
      label: humanizeOptionLabel(value, 'Event'),
    }))
  )
));

const statusSelectOptions = computed(() => (
  [{ value: '', label: 'All statuses' }].concat(
    (Array.isArray(statusOptions.value) ? statusOptions.value : []).map((value) => ({
      value: String(value),
      label: humanizeOptionLabel(value, 'Status'),
    }))
  )
));

const pageSizeSelectOptions = Object.freeze([
  { value: 10, label: '10' },
  { value: 25, label: '25' },
  { value: 50, label: '50' },
  { value: 100, label: '100' },
]);

const assignmentStatusSelectOptions = Object.freeze([
  { value: 'active', label: 'Active' },
  { value: 'paused', label: 'Paused' },
  { value: 'canceled', label: 'Canceled' },
]);

const billingStatusSelectOptions = Object.freeze([
  { value: 'pending_payment', label: 'Waiting for payment' },
  { value: 'current', label: 'Active (paid)' },
  { value: 'trialing', label: 'Trialing' },
  { value: 'canceling', label: 'Canceling (period end)' },
  { value: 'past_due', label: 'Past due' },
  { value: 'payment_failed', label: 'Payment failed' },
  { value: 'free', label: 'Free' },
]);

const normalizeBillingStatus = (value, fallback = 'pending_payment') => {
  const tokenRaw = String(value || '').trim().toLowerCase();
  if (!tokenRaw) return fallback;
  const token = tokenRaw === 'waiting_for_payment'
    ? 'pending_payment'
    : tokenRaw === 'cancelling'
      ? 'canceling'
    : tokenRaw === 'active'
      ? 'current'
      : tokenRaw;
  return billingStatusSelectOptions.some((option) => option.value === token) ? token : fallback;
};

const toBool = (value, fallback = false) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  const token = String(value ?? '').trim().toLowerCase();
  if (!token) return fallback;
  if (['1', 'true', 'yes', 'y', 'on'].includes(token)) return true;
  if (['0', 'false', 'no', 'n', 'off'].includes(token)) return false;
  return fallback;
};

const planTierStatusSelectOptions = Object.freeze([
  { value: 'active', label: 'Active' },
  { value: 'draft', label: 'Draft' },
  { value: 'archived', label: 'Archived' },
]);

const customBillingCadenceOptions = Object.freeze([
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' },
  { value: 'one_time', label: 'One-Time Lifetime Access' },
  { value: 'free_forever', label: 'Free Forever' },
]);

const cadenceSuffix = (cadence) => {
  const token = String(cadence || '').trim().toLowerCase();
  if (token === 'daily') return '/day';
  if (token === 'weekly') return '/wk';
  if (token === 'yearly') return '/yr';
  if (token === 'one_time') return ' lifetime';
  return '/mo';
};

const toSlug = (value) =>
  String(value || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/--+/g, '-')
    .slice(0, 190);

const normalizeFeatureInstanceIdList = (input) => {
  if (!Array.isArray(input)) return [];
  const seen = new Set();
  const ids = [];
  input.forEach((raw) => {
    const id = Number(raw);
    if (!Number.isInteger(id) || id <= 0 || seen.has(id)) return;
    seen.add(id);
    ids.push(id);
  });
  return ids;
};

const toAccountLabel = (account) => {
  const provider = String(account?.provider || '').trim().toLowerCase();
  if (provider === 'platforma_payments') return 'Platforma Payments';
  if (provider === 'creator_stripe') return 'Creator Stripe';
  if (provider === 'bitcoin') return 'Bitcoin';
  const fallback = String(account?.displayName || '').trim();
  return fallback || 'Payment Processor';
};

const requestBases = computed(() => {
  const rows = Array.from(new Set([apiProxyUrl, apiBackendUrl].map((value) => String(value || '').trim()).filter(Boolean)));
  return rows.length ? rows : [''];
});

const paymentAccountOptions = computed(() => {
  const rows = [{ value: '', label: 'Platforma Payments' }];
  paymentAccounts.value.forEach((account) => {
    if (!account?.id) return;
    const provider = String(account?.provider || '').trim().toLowerCase();
    if (provider === 'platforma_payments') return;
    rows.push({
      value: String(account.id),
      label: toAccountLabel(account),
    });
  });
  return rows;
});

const assignmentBadgeLabel = computed(() => {
  if (isLoadingPlan.value) return 'Loading';
  const tier = planAssignment.value?.tier;
  if (tier?.name) return `Assigned: ${tier.isCustom ? 'Custom' : tier.name}`;
  return 'No tier assigned';
});

const assignmentBadgeClass = computed(() => {
  if (planAssignment.value?.tier?.isCustom) return 'bg-purple-100 text-purple-700';
  if (planAssignment.value?.tier?.name) return 'bg-emerald-100 text-emerald-700';
  return 'bg-zinc-100 text-zinc-600';
});
const isSubscriptionCanceled = computed(
  () => String(planAssignment.value?.status || '').trim().toLowerCase() === 'canceled'
);
const creatorCancellationState = computed(() => {
  const metadata = planAssignment.value?.metadata && typeof planAssignment.value.metadata === 'object'
    ? planAssignment.value.metadata
    : {};
  const assignmentStatus = String(planAssignment.value?.status || '').trim().toLowerCase();
  const billingStatus = String(metadata?.billingStatus || '').trim().toLowerCase();
  const cancellationScheduled = !isSubscriptionCanceled.value
    && (['canceling', 'cancelling'].includes(billingStatus) || toBool(metadata?.cancelAtPeriodEnd, false));
  return {
    assignmentStatus,
    billingStatus,
    cancellationScheduled,
    cancelEffectiveAt: String(metadata?.cancelEffectiveAt || '').trim(),
    preCancelBillingStatus: String(metadata?.preCancelBillingStatus || '').trim().toLowerCase(),
  };
});
const creatorCancellationStatusLabel = computed(() => {
  if (!planAssignment.value) return 'No subscription';
  if (isSubscriptionCanceled.value) return 'Canceled';
  if (creatorCancellationState.value.cancellationScheduled) return 'Canceling';
  return 'Active';
});
const creatorCancellationStatusBadgeClass = computed(() => {
  if (!planAssignment.value) return 'bg-zinc-100 text-zinc-600';
  if (isSubscriptionCanceled.value) return 'bg-rose-100 text-rose-700';
  if (creatorCancellationState.value.cancellationScheduled) return 'bg-amber-100 text-amber-700';
  return 'bg-emerald-100 text-emerald-700';
});
const creatorCancellationEffectiveAtText = computed(() => {
  if (!creatorCancellationState.value.cancellationScheduled) return '';
  const raw = creatorCancellationState.value.cancelEffectiveAt;
  if (!raw) return '';
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return '';
  return `Effective ${date.toLocaleString()}`;
});
const canCreatorReverseCancellation = computed(() => (
  Boolean(planAssignment.value) && creatorCancellationState.value.cancellationScheduled && !isSubscriptionCanceled.value
));
const canCreatorRestoreCanceledAccount = computed(() => (
  Boolean(planAssignment.value) && isSubscriptionCanceled.value
));

const editingCustomPlan = computed(() => Number(customPlanForm.customTierId || 0) > 0);
const hasExistingCustomPlan = computed(() => {
  if (planAssignment.value?.tier?.isCustom) return true;
  const memberId = Number(props.memberId || 0);
  return (Array.isArray(planTiers.value) ? planTiers.value : []).some((tier) => (
    Boolean(tier?.isCustom) && Number(tier?.targetMemberId || 0) === memberId
  ));
});

const planTierSelectOptions = computed(() => {
  const rows = Array.isArray(planTiers.value) ? planTiers.value : [];
  return rows.map((tier) => {
    const id = Number(tier?.id || 0);
    const cadence = String(tier?.billingCadence || 'monthly').trim().toLowerCase();
    const amount = Number(tier?.priceCents || 0) / 100;
    const currency = String(tier?.currencyCode || 'USD').trim().toUpperCase() || 'USD';
    const priceText = cadence === 'free_forever'
      ? 'Free'
      : `${currency} ${amount.toFixed(2)}${cadenceSuffix(cadence)}`;
    const customText = tier?.isCustom ? ' (Custom)' : '';
    return {
      value: String(id),
      label: `${String(tier?.name || 'Untitled tier').trim() || 'Untitled tier'}${customText} - ${priceText}`,
    };
  });
});

const showNotice = (message) => {
  planNoticeMessage.value = String(message || '').trim();
  if (planNoticeTimer) {
    clearTimeout(planNoticeTimer);
    planNoticeTimer = null;
  }
  if (!planNoticeMessage.value) return;
  planNoticeTimer = setTimeout(() => {
    planNoticeMessage.value = '';
    planNoticeTimer = null;
  }, 8000);
};

const creatorPaymentPathCandidates = (path) => {
  const suffix = String(path || '').startsWith('/') ? String(path || '') : `/${String(path || '')}`;
  return [`/api/creator-payments${suffix}`, `/api/payments${suffix}`];
};

const requestCreatorPayments = async ({ method = 'GET', path, body = null } = {}) => {
  const paths = creatorPaymentPathCandidates(path);
  let response = null;
  let payload = {};
  let lastError = '';
  for (const base of requestBases.value) {
    for (const candidate of paths) {
      response = await fetch(`${base}${candidate}`, {
        method,
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: body == null ? undefined : JSON.stringify(body),
      });
      payload = await response.json().catch(() => ({}));
      if (response.ok && payload?.success !== false) {
        return payload;
      }
      lastError = String(payload?.message || payload?.error || `Request failed (${response.status})`);
      if (response.status !== 404) {
        break;
      }
    }
    if (response?.ok && payload?.success !== false) break;
  }
  throw new Error(lastError || `Request failed (${response?.status || 500})`);
};

const loadPaymentSettings = async () => {
  isLoadingPaymentSettings.value = true;
  try {
    const payload = await requestCreatorPayments({
      method: 'GET',
      path: '/creator/settings',
    });
    paymentAccounts.value = (Array.isArray(payload?.accounts) ? payload.accounts : []).filter(
      (account) => String(account?.status || '').trim().toLowerCase() === 'active'
    );
    if (!customPlanForm.paymentAccountId) {
      customPlanForm.paymentAccountId = '';
    } else {
      const selected = paymentAccounts.value.find((account) => String(account?.id || '') === String(customPlanForm.paymentAccountId));
      if (String(selected?.provider || '').trim().toLowerCase() === 'platforma_payments') {
        customPlanForm.paymentAccountId = '';
      }
    }
  } catch (_error) {
    paymentAccounts.value = [];
  } finally {
    isLoadingPaymentSettings.value = false;
  }
};

const loadProductFeatures = async () => {
  isLoadingProductFeatures.value = true;
  try {
    let response = null;
    let payload = {};
    let lastError = '';
    for (const base of requestBases.value) {
      response = await fetch(`${base}/api/feature-instances`, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      payload = await response.json().catch(() => ({}));
      if (response.ok && payload?.success !== false) break;
      lastError = String(payload?.message || payload?.error || `Failed loading feature instances (${response.status})`);
      if (response.status !== 404) break;
    }

    if (!response || !response.ok || payload?.success === false) {
      throw new Error(lastError || `Failed loading feature instances (${response?.status || 500})`);
    }

    const rows = Array.isArray(payload?.instances) ? payload.instances : [];
    productFeatureRows.value = rows
      .map((row) => {
        const instanceId = Number(row?.instance_id ?? row?.instanceId ?? 0);
        if (!Number.isInteger(instanceId) || instanceId <= 0) return null;
        const displayName = String(
          row?.custom_name ??
          row?.customName ??
          row?.feature_name ??
          row?.featureName ??
          row?.slug ??
          `Instance ${instanceId}`
        ).trim() || `Instance ${instanceId}`;
        const slug = String(row?.slug || '').trim();
        const order = Number(row?.navigation_order ?? row?.navigationOrder ?? 0);
        return {
          instanceId,
          displayName,
          slugLabel: slug ? `/${slug}` : `Instance #${instanceId}`,
          order: Number.isFinite(order) ? order : 0,
        };
      })
      .filter(Boolean)
      .sort((a, b) => {
        if (a.order !== b.order) return a.order - b.order;
        return String(a.displayName).localeCompare(String(b.displayName));
      });
    const validIds = new Set(productFeatureRows.value.map((row) => row.instanceId));
    customSelectedFeatureIds.value = normalizeFeatureInstanceIdList(customSelectedFeatureIds.value).filter((id) => validIds.has(id));
  } catch (_error) {
    productFeatureRows.value = [];
  } finally {
    isLoadingProductFeatures.value = false;
  }
};

const resetCustomPlanForm = () => {
  customPlanForm.customTierId = 0;
  customPlanForm.name = '';
  customPlanForm.slug = '';
  customPlanForm.billingCadence = 'monthly';
  customPlanForm.price = '0.00';
  customPlanForm.currencyCode = 'USD';
  customPlanForm.freeTrialEnabled = false;
  customPlanForm.trialDays = '0';
  customPlanForm.status = 'active';
  customPlanForm.description = '';
  customPlanForm.coverImageUrl = '';
  customPlanForm.askShippingAddress = false;
  customPlanForm.limitMembersEnabled = false;
  customPlanForm.memberLimitCount = '';
  customPlanForm.paymentAccountId = '';
  customSelectedFeatureIds.value = [];
  customBenefitsOpen.value = false;
  customAdvancedOpen.value = false;
  customPlanSlugManuallyEdited.value = false;
};

const applyCustomTierToForm = (tier) => {
  resetCustomPlanForm();
  if (!tier || typeof tier !== 'object') return;
  customPlanForm.customTierId = Number(tier?.id || 0);
  customPlanForm.name = String(tier?.name || '').trim();
  customPlanForm.slug = String(tier?.slug || '').trim();
  customPlanForm.billingCadence = String(tier?.billingCadence || 'monthly').trim().toLowerCase() || 'monthly';
  customPlanForm.price = (Number(tier?.priceCents || 0) / 100).toFixed(2);
  customPlanForm.currencyCode = String(tier?.currencyCode || 'USD').trim().toUpperCase() || 'USD';
  customPlanForm.freeTrialEnabled = Number(tier?.trialDays || 0) > 0;
  customPlanForm.trialDays = Number(tier?.trialDays || 0) > 0 ? String(Number(tier.trialDays)) : '7';
  customPlanForm.status = String(tier?.status || 'active').trim().toLowerCase() || 'active';
  customPlanForm.description = String(tier?.description || '').trim();
  customPlanForm.coverImageUrl = String(tier?.coverImageUrl || '').trim();
  customPlanForm.askShippingAddress = Boolean(tier?.askShippingAddress);
  customPlanForm.limitMembersEnabled = Number(tier?.memberLimitCount || 0) > 0;
  customPlanForm.memberLimitCount = Number(tier?.memberLimitCount || 0) > 0 ? String(Number(tier.memberLimitCount)) : '';
  const existingPaymentAccountId = tier?.paymentAccountId ? String(tier.paymentAccountId) : '';
  const selectedProvider = String(tier?.selectedProvider || '').trim().toLowerCase();
  if (selectedProvider === 'platforma_payments') {
    customPlanForm.paymentAccountId = '';
  } else {
    customPlanForm.paymentAccountId = existingPaymentAccountId;
  }
  const metadata = tier && typeof tier.metadata === 'object' && tier.metadata ? tier.metadata : {};
  const metadataFeatureIds = metadata?.selectedFeatureInstanceIds ?? metadata?.featureInstanceIds ?? [];
  customSelectedFeatureIds.value = normalizeFeatureInstanceIdList(metadataFeatureIds);
  customPlanSlugManuallyEdited.value = true;
};

const hydrateCancellationOptionsFromAssignment = (assignment) => {
  const metadata = assignment?.metadata && typeof assignment.metadata === 'object'
    ? assignment.metadata
    : {};
  const billingStatus = String(metadata?.billingStatus || '').trim().toLowerCase();
  cancellationMode.value = toBool(metadata?.cancelAtPeriodEnd, false)
    || billingStatus === 'canceling'
    || billingStatus === 'cancelling'
    ? 'period_end'
    : 'immediate';
  showCancellationRestoreBanner.value = toBool(metadata?.showCancellationRestoreBanner, true);
  allowCancellationRestore.value = toBool(metadata?.allowCancellationRestore, true);
  cancellationOptionsError.value = '';
};

const hydratePlanState = (payload) => {
  memberProfile.value = payload?.member && typeof payload.member === 'object' ? payload.member : memberProfile.value;
  planTiers.value = Array.isArray(payload?.tiers) ? payload.tiers : [];
  planAssignment.value = payload?.assignment && typeof payload.assignment === 'object' ? payload.assignment : null;
  selectedPlanTierId.value = planAssignment.value?.planTierId ? String(planAssignment.value.planTierId) : '';
  selectedAssignmentStatus.value = String(planAssignment.value?.status || 'active').trim().toLowerCase() || 'active';
  selectedBillingStatus.value = normalizeBillingStatus(planAssignment.value?.metadata?.billingStatus, 'pending_payment');
  hydrateCancellationOptionsFromAssignment(planAssignment.value);
};

const loadMemberPlanData = async () => {
  const memberId = Number(props.memberId || 0);
  if (!Number.isInteger(memberId) || memberId <= 0) {
    planTiers.value = [];
    planAssignment.value = null;
    selectedPlanTierId.value = '';
    selectedAssignmentStatus.value = 'active';
    selectedBillingStatus.value = 'pending_payment';
    showCustomPlanEditor.value = false;
    showCancelSubscriptionOptions.value = false;
    cancellationMode.value = 'period_end';
    showCancellationRestoreBanner.value = true;
    allowCancellationRestore.value = true;
    cancellationOptionsError.value = '';
    resetCustomPlanForm();
    return;
  }

  isLoadingPlan.value = true;
  planErrorMessage.value = '';
  try {
    const payload = await requestCreatorPayments({
      method: 'GET',
      path: `/creator/members/${encodeURIComponent(String(memberId))}/plan-subscription`,
    });
    hydratePlanState(payload);
  } catch (error) {
    planErrorMessage.value = String(error?.message || 'Failed loading member subscription tier assignment.');
  } finally {
    isLoadingPlan.value = false;
  }
};

const saveTierAssignment = async (statusOverride = '', bodyOverrides = {}, successMessage = '') => {
  const memberId = Number(props.memberId || 0);
  if (!Number.isInteger(memberId) || memberId <= 0) return false;
  isSavingPlanAssignment.value = true;
  planErrorMessage.value = '';
  let saved = false;
  try {
    const nextStatus = String(statusOverride || selectedAssignmentStatus.value || 'active').trim().toLowerCase() || 'active';
    const requestBody = {
      planTierId: selectedPlanTierId.value ? Number(selectedPlanTierId.value) : null,
      status: nextStatus,
      billingStatus: normalizeBillingStatus(selectedBillingStatus.value, 'pending_payment'),
      ...(bodyOverrides && typeof bodyOverrides === 'object' ? bodyOverrides : {}),
    };
    const payload = await requestCreatorPayments({
      method: 'PUT',
      path: `/creator/members/${encodeURIComponent(String(memberId))}/plan-subscription`,
      body: requestBody,
    });
    hydratePlanState(payload);
    await loadSubscriptionData();
    if (successMessage) {
      showNotice(successMessage);
    } else {
      showNotice(nextStatus === 'canceled' ? 'Member subscription canceled' : 'Assignment saved');
    }
    saved = true;
  } catch (error) {
    planErrorMessage.value = String(error?.message || 'Failed to save tier assignment.');
  } finally {
    isSavingPlanAssignment.value = false;
  }
  return saved;
};

const cancelMemberSubscription = () => {
  if (isSavingPlanAssignment.value || isSubmittingCancellation.value) return;
  cancellationOptionsError.value = '';
  showCancelSubscriptionOptions.value = !showCancelSubscriptionOptions.value;
};

const submitCreatorCancellation = async () => {
  if (isSubmittingCancellation.value || isSavingPlanAssignment.value) return;
  isSubmittingCancellation.value = true;
  cancellationOptionsError.value = '';
  try {
    const mode = cancellationMode.value === 'immediate' ? 'immediate' : 'period_end';
    const saved = await saveTierAssignment(
      'canceled',
      {
        cancellationMode: mode,
        showCancellationRestoreBanner: Boolean(showCancellationRestoreBanner.value),
        allowCancellationRestore: Boolean(allowCancellationRestore.value),
      },
      mode === 'immediate'
        ? 'Member subscription canceled immediately'
        : 'Member cancellation scheduled for period end'
    );
    if (!saved) {
      cancellationOptionsError.value = String(planErrorMessage.value || 'Unable to save cancellation options.');
      return;
    }
    showCancelSubscriptionOptions.value = false;
  } finally {
    isSubmittingCancellation.value = false;
  }
};

const reverseCreatorCancellation = async () => {
  if (isSubmittingCancellation.value || isSavingPlanAssignment.value || !canCreatorReverseCancellation.value) return;
  isSubmittingCancellation.value = true;
  cancellationOptionsError.value = '';
  try {
    const saved = await saveTierAssignment(
      'active',
      {
        restoreCancellation: true,
      },
      'Scheduled cancellation reversed'
    );
    if (!saved) {
      cancellationOptionsError.value = String(planErrorMessage.value || 'Unable to reverse cancellation.');
      return;
    }
    showCancelSubscriptionOptions.value = false;
  } finally {
    isSubmittingCancellation.value = false;
  }
};

const restoreCanceledSubscriptionByCreator = async () => {
  if (isSubmittingCancellation.value || isSavingPlanAssignment.value || !canCreatorRestoreCanceledAccount.value) return;
  isSubmittingCancellation.value = true;
  cancellationOptionsError.value = '';
  try {
    const saved = await saveTierAssignment(
      'active',
      {
        restoreCanceledAccount: true,
        billingStatus: 'pending_payment',
      },
      'Member account restored. Payment is required before access resumes.'
    );
    if (!saved) {
      cancellationOptionsError.value = String(planErrorMessage.value || 'Unable to restore canceled account.');
      return;
    }
    showCancelSubscriptionOptions.value = false;
  } finally {
    isSubmittingCancellation.value = false;
  }
};

const openCustomPlanEditor = async () => {
  const memberId = Number(props.memberId || 0);
  const selectedTier = planTiers.value.find((tier) => Number(tier?.id || 0) === Number(selectedPlanTierId.value || 0));
  const existingMemberCustomTier = planTiers.value.find((tier) => (
    Boolean(tier?.isCustom) && Number(tier?.targetMemberId || 0) === memberId
  ));
  if (selectedTier?.isCustom) {
    applyCustomTierToForm(selectedTier);
  } else if (planAssignment.value?.tier?.isCustom) {
    applyCustomTierToForm(planAssignment.value.tier);
  } else if (existingMemberCustomTier?.id) {
    applyCustomTierToForm(existingMemberCustomTier);
  } else {
    resetCustomPlanForm();
    const firstName = String(memberProfile.value?.firstName || '').trim();
    customPlanForm.name = firstName ? `${firstName} custom plan` : 'Custom member plan';
    customPlanForm.slug = toSlug(customPlanForm.name || '');
  }
  await Promise.all([
    loadPaymentSettings(),
    loadProductFeatures(),
  ]);
  showCustomPlanEditor.value = true;
};

const closeCustomPlanEditor = () => {
  showCustomPlanEditor.value = false;
  resetCustomPlanForm();
};

const toggleCustomFreeTrial = () => {
  if (['free_forever', 'one_time'].includes(customPlanForm.billingCadence)) return;
  customPlanForm.freeTrialEnabled = !customPlanForm.freeTrialEnabled;
  if (!customPlanForm.freeTrialEnabled) {
    customPlanForm.trialDays = '7';
  }
};

const saveCustomPlan = async () => {
  const memberId = Number(props.memberId || 0);
  if (!Number.isInteger(memberId) || memberId <= 0) return;
  const name = String(customPlanForm.name || '').trim();
  if (!name) {
    planErrorMessage.value = 'Custom plan name is required.';
    return;
  }

  const cadence = String(customPlanForm.billingCadence || 'monthly').trim().toLowerCase() || 'monthly';
  const numericPrice = Number(customPlanForm.price || 0);
  const safePrice = Number.isFinite(numericPrice) ? Math.max(0, numericPrice) : 0;
  const priceCents = cadence === 'free_forever' ? 0 : Math.round(safePrice * 100);
  const trialDaysRaw = Number(customPlanForm.trialDays || 1);
  const trialDays = ['free_forever', 'one_time'].includes(cadence) || !customPlanForm.freeTrialEnabled
    ? 0
    : Math.max(1, Math.trunc(Number.isFinite(trialDaysRaw) ? trialDaysRaw : 1));
  const memberLimitCount = customPlanForm.limitMembersEnabled
    ? Math.max(1, Math.trunc(Number(customPlanForm.memberLimitCount || 1)))
    : null;
  const selectedIds = normalizeFeatureInstanceIdList(customSelectedFeatureIds.value);
  const labelById = new Map(
    (Array.isArray(productFeatureRows.value) ? productFeatureRows.value : []).map((row) => [row.instanceId, row.displayName])
  );
  const selectedLabels = selectedIds
    .map((id) => String(labelById.get(id) || '').trim())
    .filter(Boolean);

  isSavingCustomPlan.value = true;
  planErrorMessage.value = '';
  try {
    const payload = await requestCreatorPayments({
      method: 'POST',
      path: `/creator/members/${encodeURIComponent(String(memberId))}/plan-subscription/custom`,
      body: {
        customTierId: Number(customPlanForm.customTierId || 0) || undefined,
        name,
        slug: String(customPlanForm.slug || '').trim(),
        billingCadence: cadence,
        priceCents,
        currencyCode: String(customPlanForm.currencyCode || 'USD').trim().toUpperCase() || 'USD',
        trialDays,
        status: String(customPlanForm.status || 'active').trim().toLowerCase() || 'active',
        description: String(customPlanForm.description || '').trim(),
        coverImageUrl: String(customPlanForm.coverImageUrl || '').trim(),
        askShippingAddress: Boolean(customPlanForm.askShippingAddress),
        memberLimitCount,
        paymentAccountId: customPlanForm.paymentAccountId ? Number(customPlanForm.paymentAccountId) : null,
        benefits: selectedLabels,
        metadata: {
          selectedFeatureInstanceIds: selectedIds,
        },
        assignToMember: true,
      },
    });
    hydratePlanState(payload);
    if (payload?.tier?.id) {
      selectedPlanTierId.value = String(payload.tier.id);
    }
    await loadSubscriptionData();
    showCustomPlanEditor.value = false;
    resetCustomPlanForm();
    showNotice('Custom plan saved');
  } catch (error) {
    planErrorMessage.value = String(error?.message || 'Failed to save custom plan.');
  } finally {
    isSavingCustomPlan.value = false;
  }
};

const loadSubscriptionData = async () => {
  const memberId = Number(props.memberId || 0);
  if (!Number.isInteger(memberId) || memberId <= 0) {
    nextCharge.value = null;
    eventRows.value = [];
    eventTypeOptions.value = [];
    statusOptions.value = [];
    totalCount.value = 0;
    totalPages.value = 1;
    return;
  }

  isLoading.value = true;
  errorMessage.value = '';
  try {
    const query = new URLSearchParams({
      page: String(currentPage.value),
      pageSize: String(pageSize.value),
    });
    if (String(searchQuery.value || '').trim()) query.set('q', String(searchQuery.value || '').trim());
    if (String(selectedEventType.value || '').trim()) query.set('eventType', String(selectedEventType.value || '').trim());
    if (String(selectedStatus.value || '').trim()) query.set('status', String(selectedStatus.value || '').trim());

    const payload = await requestCreatorPayments({
      method: 'GET',
      path: `/creator/members/${encodeURIComponent(String(memberId))}/subscription-payment-attempts?${query.toString()}`,
    });

    nextCharge.value = payload?.nextCharge && typeof payload.nextCharge === 'object'
      ? payload.nextCharge
      : null;
    syncRescheduleInputFromNextCharge();
    eventRows.value = Array.isArray(payload?.events) ? payload.events : [];
    eventTypeOptions.value = Array.isArray(payload?.eventTypeOptions) ? payload.eventTypeOptions : [];
    statusOptions.value = Array.isArray(payload?.statusOptions) ? payload.statusOptions : [];
    totalCount.value = Number(payload?.pagination?.totalCount || 0);
    totalPages.value = Math.max(1, Number(payload?.pagination?.totalPages || 1));

    if (currentPage.value > totalPages.value) {
      currentPage.value = totalPages.value;
    }
  } catch (error) {
    errorMessage.value = String(error?.message || 'Failed loading subscription payment attempts.');
    nextCharge.value = null;
    syncRescheduleInputFromNextCharge();
    eventRows.value = [];
    eventTypeOptions.value = [];
    statusOptions.value = [];
    totalCount.value = 0;
    totalPages.value = 1;
  } finally {
    isLoading.value = false;
  }
};

const nextPage = () => {
  if (currentPage.value >= totalPages.value) return;
  currentPage.value += 1;
};

const prevPage = () => {
  if (currentPage.value <= 1) return;
  currentPage.value -= 1;
};

const parseCreatorLocalDate = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return null;
  const normalized = raw.replace(' ', 'T');
  const hasTimezone = /([zZ]|[+-]\d{2}:?\d{2})$/.test(normalized);
  const date = new Date(hasTimezone ? normalized : `${normalized}Z`);
  if (Number.isNaN(date.getTime())) return null;
  return date;
};

const formatDateTime = (value) => {
  const date = parseCreatorLocalDate(value);
  if (!date || Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  });
};

const formatAmount = (amountCents, currencyCode) => {
  if (amountCents == null || amountCents === '') return '-';
  const amount = Number(amountCents || 0) / 100;
  const currency = String(currencyCode || 'USD').trim().toUpperCase() || 'USD';
  return `${currency} ${amount.toFixed(2)}`;
};

const toLocalDatetimeInputValue = (value) => {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const syncRescheduleInputFromNextCharge = () => {
  const dueAtRaw = String(nextCharge.value?.dueAt || '').trim();
  if (!dueAtRaw) {
    rescheduleNextChargeLocal.value = '';
    return;
  }
  const nextValue = toLocalDatetimeInputValue(dueAtRaw);
  if (nextValue) {
    rescheduleNextChargeLocal.value = nextValue;
  }
};

const closeReschedulePicker = () => {
  showReschedulePicker.value = false;
  rescheduleNextChargeError.value = '';
  rescheduleNextChargeNotice.value = '';
};

const saveRescheduledNextCharge = async (nextBillingAtOverride = '') => {
  const memberId = Number(props.memberId || 0);
  if (!Number.isInteger(memberId) || memberId <= 0) return;

  const rawOverride = String(nextBillingAtOverride || '').trim();
  const rawInput = rawOverride || String(rescheduleNextChargeLocal.value || '').trim();
  let parsed = null;
  if (rawOverride) {
    const normalized = rawOverride.replace(' ', 'T');
    parsed = normalized ? new Date(`${normalized}Z`) : null;
  } else {
    parsed = rawInput ? new Date(rawInput) : null;
  }
  if (!parsed || Number.isNaN(parsed.getTime())) {
    rescheduleNextChargeError.value = 'Please choose a valid date and time.';
    rescheduleNextChargeNotice.value = '';
    return;
  }
  if (parsed.getTime() <= Date.now()) {
    rescheduleNextChargeError.value = 'Next charge time must be in the future.';
    rescheduleNextChargeNotice.value = '';
    return;
  }

  isSavingRescheduleNextCharge.value = true;
  rescheduleNextChargeError.value = '';
  rescheduleNextChargeNotice.value = '';
  planErrorMessage.value = '';
  try {
    const payload = await requestCreatorPayments({
      method: 'PUT',
      path: `/creator/members/${encodeURIComponent(String(memberId))}/plan-subscription/reschedule`,
      body: {
        nextBillingAt: parsed.toISOString(),
        activateForAutoCharge: false,
      },
    });
    if (payload?.assignment && typeof payload.assignment === 'object') {
      planAssignment.value = payload.assignment;
      selectedPlanTierId.value = planAssignment.value?.planTierId ? String(planAssignment.value.planTierId) : '';
      selectedAssignmentStatus.value = String(planAssignment.value?.status || 'active').trim().toLowerCase() || 'active';
      selectedBillingStatus.value = normalizeBillingStatus(planAssignment.value?.metadata?.billingStatus, 'pending_payment');
      hydrateCancellationOptionsFromAssignment(planAssignment.value);
    }
    if (payload?.nextCharge && typeof payload.nextCharge === 'object') {
      nextCharge.value = payload.nextCharge;
    }
    await loadSubscriptionData();
    syncRescheduleInputFromNextCharge();
    rescheduleNextChargeNotice.value = 'Next charge schedule updated.';
    showReschedulePicker.value = false;
    showNotice('Next charge rescheduled');
  } catch (error) {
    rescheduleNextChargeError.value = String(error?.message || 'Failed to reschedule next charge.');
    rescheduleNextChargeNotice.value = '';
  } finally {
    isSavingRescheduleNextCharge.value = false;
  }
};

const handleRescheduleSchedule = async (sqlDateTimeUtc) => {
  await saveRescheduledNextCharge(sqlDateTimeUtc);
};

const statusBadgeClass = (status) => {
  const token = String(status || '').trim().toLowerCase();
  if (token === 'succeeded' || token === 'paid' || token === 'active') {
    return 'bg-emerald-50 text-emerald-700 ring-emerald-700/10';
  }
  if (token === 'pending' || token === 'processing') {
    return 'bg-amber-50 text-amber-700 ring-amber-700/10';
  }
  if (token === 'failed' || token === 'canceled' || token === 'cancelled') {
    return 'bg-rose-50 text-rose-700 ring-rose-700/10';
  }
  return 'bg-zinc-50 text-zinc-700 ring-zinc-700/10';
};

watch(
  () => customPlanForm.name,
  (nextName) => {
    if (customPlanSlugManuallyEdited.value) return;
    customPlanForm.slug = toSlug(nextName || '');
  }
);

watch(
  () => customPlanForm.billingCadence,
  (nextCadence) => {
    if (nextCadence === 'free_forever' || nextCadence === 'one_time') {
      if (nextCadence === 'free_forever') customPlanForm.price = '0.00';
      customPlanForm.freeTrialEnabled = false;
      customPlanForm.trialDays = '7';
    }
  }
);

watch(
  () => customPlanForm.limitMembersEnabled,
  (enabled) => {
    if (!enabled) {
      customPlanForm.memberLimitCount = '';
    }
  }
);

watch(
  () => props.memberId,
  () => {
    currentPage.value = 1;
    rescheduleNextChargeError.value = '';
    rescheduleNextChargeNotice.value = '';
    rescheduleNextChargeLocal.value = '';
    showReschedulePicker.value = false;
    showCancelSubscriptionOptions.value = false;
    cancellationOptionsError.value = '';
    void loadSubscriptionData();
    void loadMemberPlanData();
  },
  { immediate: true }
);

watch([currentPage, pageSize], () => {
  void loadSubscriptionData();
});

watch([searchQuery, selectedEventType, selectedStatus], () => {
  currentPage.value = 1;
  void loadSubscriptionData();
});

watch(rescheduleNextChargeLocal, () => {
  rescheduleNextChargeError.value = '';
  rescheduleNextChargeNotice.value = '';
});

onBeforeUnmount(() => {
  if (planNoticeTimer) {
    clearTimeout(planNoticeTimer);
    planNoticeTimer = null;
  }
});
</script>

